import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { Search, Globe, Calendar, ChevronRight, Car } from 'lucide-react';
import { useTranslation } from '../LanguageContext';

const WikiPage = () => {
  const { t } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/wiki/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    const searchWiki = async () => {
      if (searchQuery.length < 2) {
        setSearchResults(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/wiki/search?q=${searchQuery}`);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Error searching:', error);
      }
    };

    const debounce = setTimeout(searchWiki, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const filteredBrands = searchQuery && !searchResults
    ? brands.filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : brands;

  // Group brands by country
  const brandsByCountry = filteredBrands.reduce((acc, brand) => {
    if (!acc[brand.country]) acc[brand.country] = [];
    acc[brand.country].push(brand);
    return acc;
  }, {});

  const countryFlags = {
    'Germany': '🇩🇪',
    'USA': '🇺🇸',
    'Japan': '🇯🇵',
    'Italy': '🇮🇹',
    'UK': '🇬🇧',
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase mb-4">
            {t('wiki.title1')} <span className="text-cyan-500 neon-text">{t('wiki.title2')}</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('wiki.desc')}
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder={t('wikiPage.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 bg-black/50 border-white/10 text-white placeholder:text-gray-600 focus:border-cyan-500 text-lg"
              data-testid="wiki-search-input"
            />
          </div>

          {/* Search Results Dropdown */}
          {searchResults && (searchResults.brands.length > 0 || searchResults.models.length > 0) && (
            <div className="absolute z-50 w-full max-w-xl mt-2 glass-card rounded-xl overflow-hidden animate-slide-up">
              {searchResults.brands.length > 0 && (
                <div className="p-4 border-b border-white/10">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{t('wikiPage.brands')}</p>
                  {searchResults.brands.map(brand => (
                    <Link
                      key={brand.id}
                      to={`/wiki/${brand.name}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <Car className="w-5 h-5 text-cyan-500" />
                      <span>{brand.name}</span>
                      <span className="text-gray-500 text-sm">{brand.country}</span>
                    </Link>
                  ))}
                </div>
              )}
              {searchResults.models.length > 0 && (
                <div className="p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{t('wikiPage.models')}</p>
                  {searchResults.models.map(model => (
                    <Link
                      key={model.id}
                      to={`/wiki/model/${model.id}`}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <Car className="w-5 h-5 text-gray-400" />
                      <span>{model.brand} {model.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">{brands.length}+</p>
            <p className="text-sm text-gray-400">{t('wikiPage.brands')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">500+</p>
            <p className="text-sm text-gray-400">{t('wikiPage.modelsCount')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">100+</p>
            <p className="text-sm text-gray-400">{t('wikiPage.yearsCovered')}</p>
          </div>
          <div className="glass-card rounded-xl p-6 text-center">
            <p className="text-3xl font-bold text-cyan-500 mb-1">20+</p>
            <p className="text-sm text-gray-400">{t('wikiPage.countries')}</p>
          </div>
        </div>

        {/* Brands by Country */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-xl bg-white/5 h-32 animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(brandsByCountry).map(([country, countryBrands]) => (
              <div key={country}>
                <h2 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-3">
                  <span className="text-2xl">{countryFlags[country] || <Globe className="w-6 h-6 text-cyan-500" />}</span>
                  {country}
                  <span className="text-sm text-gray-500 font-normal">({countryBrands.length} brands)</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {countryBrands.map(brand => (
                    <Link
                      key={brand.id}
                      to={`/wiki/${brand.name}`}
                      className="glass-card rounded-xl p-6 hover:border-cyan-500/50 transition-all group"
                      data-testid={`brand-card-${brand.name}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center overflow-hidden">
                            {brand.logo_url ? (
                              <img 
                                src={brand.logo_url} 
                                alt={brand.name}
                                className="w-10 h-10 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <Car className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-heading text-lg font-semibold group-hover:text-cyan-500 transition-colors">
                              {brand.name}
                            </h3>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {t('wikiPage.founded')} {brand.founded}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-cyan-500 transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WikiPage;
